import type { ReactNode } from "react";

export interface FieldShellProps {
  /** The field label, rendered above the control. */
  label: ReactNode;
  /**
   * When true, a danger-toned required asterisk is appended after the label —
   * mirrors Jmix's `setRequiredIndicatorVisible(true)`.
   */
  required?: boolean;
  /** Optional leading glyph shown before the label text (primary-toned, e.g. a paw/calendar icon). */
  icon?: ReactNode;
  /** When true, the field spans both columns of a 2-column field grid (`grid-column: 1 / -1`). */
  span2?: boolean;
  /** Extra class names merged onto the root — for host layout modifiers. */
  className?: string;
  /** The control(s) hosted below the label: any input, select, textarea, switch, date picker, etc. */
  children: ReactNode;
}

/**
 * A labelled wrapper around an arbitrary form control — the "label-above-control" vocabulary used
 * across the SPA's bespoke forms.
 *
 * Unlike {@link Field}/{@link SelectField} (which render their own `<input>`/`<select>`), `FieldShell`
 * only provides the label, an optional leading icon, an optional required asterisk and a column-span
 * affordance, then slots whatever control you pass as `children`. Reach for it when the control isn't
 * a plain text input: a `<select>` with custom options, a `<textarea>`, a Switch/toggle, a date
 * picker, or a multi-control row. It carries no `margin-bottom` (unlike `.fui-field`), so it composes
 * inside flex/grid form layouts that own their own gap.
 */
export function FieldShell({ label, required, icon, span2, className, children }: FieldShellProps) {
  const rootClass =
    "fui-field-shell" +
    (span2 ? " fui-field-shell--span2" : "") +
    (className ? ` ${className}` : "");
  return (
    <div className={rootClass}>
      <span className="fui-field-shell__label">
        {icon ? (
          <span className="fui-field-shell__icon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="fui-field-shell__text">
          {label}
          {required ? (
            <span className="fui-field-shell__req" aria-hidden="true">
              {" *"}
            </span>
          ) : null}
        </span>
      </span>
      {children}
    </div>
  );
}
